import { cn } from '@/utils/cn';
import { SeverityPill } from './SeverityPill';
import type { AlarmHistoryEvent, Transition } from '@/types/alarm.types';

interface Props {
  events: AlarmHistoryEvent[];
  title?: string;
  onViewAll?: () => void;
  className?: string;
}

const TRANSITION_GLYPH: Record<Transition, string> = {
  raised: 'raised',
  cleared: 'cleared',
  underlying_clear: 'cleared',
  acknowledged: 'acked',
  state_change: 'changed',
  communication_loss: 'comm-lost',
  communication_restored: 'comm-ok',
};

export function RunningLogCard({
  events,
  title = 'Recent events',
  onViewAll,
  className,
}: Props) {
  return (
    <section
      role="region"
      aria-label={title}
      className={cn('bg-ems-bg-card border border-ems-border rounded p-3', className)}
    >
      <header className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ems-text-secondary">
          {title}
        </h3>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-xs text-ems-blue hover:underline focus:outline-none focus:ring-2 focus:ring-ems-blue/40 rounded"
          >
            View all →
          </button>
        )}
      </header>
      {events.length === 0 ? (
        <p className="text-xs text-ems-text-muted py-4 text-center">No recent events</p>
      ) : (
        <ul className="space-y-1.5">
          {events.map((e) => (
            <li
              key={e.event_id}
              className="flex items-center gap-2 text-xs"
              data-transition={e.transition}
            >
              <SeverityPill severity={e.severity} variant="icon" className="shrink-0" />
              <span className="text-ems-text-muted shrink-0 w-16 uppercase tracking-wide text-[10px]">
                {TRANSITION_GLYPH[e.transition]}
              </span>
              <span className="text-ems-text-primary truncate min-w-0">
                {e.device_id} · {e.condition_label}
              </span>
              <span className="text-ems-text-muted shrink-0 ml-auto whitespace-nowrap">
                {formatTime(e.ts)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
